import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Textarea } from '../../../shared/components/Textarea';
import { useToast } from '../../../shared/components/Toast';
import { mensagemDeErro } from '../api';
import { ReinstatementItem } from '../api/desligamento';
import { REINSTATEMENT_STEP_LABELS, formatDate } from '../components/format';
import { PessoasModal } from '../components/PessoasModal';
import { useDecidirReintegracao, useReintegracoesPendentes } from '../hooks/desligamento';

const DECISOES = [
  { value: 'aprovado', label: 'Aprovar' },
  { value: 'complementacao', label: 'Solicitar complementação' },
  { value: 'rejeitado', label: 'Rejeitar' },
];

/** Reintegrações aguardando a etapa que o usuário decide (RH/gestor ou jurídico). */
export default function ReintegracoesPendentesPage() {
  const toast = useToast();
  const pendentes = useReintegracoesPendentes();
  const decidir = useDecidirReintegracao();
  const [item, setItem] = useState<ReinstatementItem | null>(null);
  const [decision, setDecision] = useState('aprovado');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!item) return;
    if (decision !== 'aprovado' && !comment.trim()) return setError('Informe o comentário.');
    try {
      const r = await decidir.mutateAsync({ id: item.id, decision, comment: comment.trim() || undefined });
      setItem(null);
      toast.success(r.status === 'aprovada' ? 'Reintegração aprovada: o vínculo foi reaberto.' : 'Decisão registrada.');
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível registrar a decisão.'));
    }
  }

  const lista = pendentes.data ?? [];
  return (
    <PageContainer title="Reintegrações pendentes" breadcrumbs={[{ label: 'Pessoas' }, { label: 'Reintegrações pendentes' }]}>
      {lista.length === 0 ? (
        <EmptyState title="Nenhuma reintegração aguardando você" />
      ) : (
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
          {lista.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
              <div>
                <p className="font-medium text-gray-900">
                  <Link className="text-blue-700 hover:underline" to={`/pessoas/colaboradores/${r.userId}`}>{r.personName}</Link>
                  {' '}· matrícula {r.internalRegistration} · {r.reason.name} · etapa {REINSTATEMENT_STEP_LABELS[r.currentStep ?? 'rh']}
                </p>
                <p className="text-gray-700">A partir de {formatDate(r.reinstatementDate)} · solicitado por {r.requestedByName ?? '—'}</p>
                <p className="text-xs text-gray-500">{r.justification}</p>
              </div>
              <Button size="sm" onClick={() => { setDecision('aprovado'); setComment(''); setError(null); setItem(r); }}>Decidir</Button>
            </li>
          ))}
        </ul>
      )}
      <PessoasModal open={item !== null} title="Decidir reintegração" submitLabel="Registrar"
        submitVariant={decision === 'rejeitado' ? 'danger' : 'primary'} isSubmitting={decidir.isPending} submitError={error}
        onClose={() => setItem(null)} onSubmit={submit}>
        <AutocompleteSelect label="Decisão" options={DECISOES} value={decision} onChange={setDecision} />
        <Textarea label={decision === 'aprovado' ? 'Parecer (opcional)' : 'Comentário'} value={comment} onChange={(e) => setComment(e.target.value)} />
      </PessoasModal>
    </PageContainer>
  );
}
